import App, { FlashApp, PqApp } from "../app";
import { FirstJumpEvent, JumpEvent, NavigateEvent, NextEvent, PreviousEvent, ReloadEvent } from "../events";
import { Annotations } from "../lib/userdata";
import { AttemptId, ItemId, ProductId, QuizId } from "../model/datasource";
import { ContentItem } from "../model/datasource/item";
import Controller from "./controller";

export abstract class NavigationController<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> extends Controller<P, A, Z, I> {
	bind(target: EventTarget, app: App<P, A, Z, I>): void {
		target.addEventListener(NextEvent.type, () => this.next(app));
		target.addEventListener(PreviousEvent.type, () => this.previous(app));
		target.addEventListener(JumpEvent.type, (e: JumpEvent) => this.jump(app, e.detail));
		target.addEventListener(ReloadEvent.type, () => this.reload(app));
		target.addEventListener(FirstJumpEvent.type, (e: FirstJumpEvent) => this.firstJump(app, e.detail));
		target.addEventListener(NavigateEvent.type, (e: NavigateEvent) => this.navigate(app, e.detail));
	}

	async next(app: App<P, A, Z, I>): Promise<void> {
		const previous: number = app.current.get();
		const next: number = app.current.next();
		const nextContent = await app.content.get(next);
		app.navigation.pushHash(app.model.list.itemIdSerializer().toString(nextContent.id));
		this.switch(app, previous, next);
	}

	async previous(app: App<P, A, Z, I>): Promise<void> {
		const previous: number = app.current.get();
		const next: number = app.current.previous();
		const nextContent = await app.content.get(next);
		app.navigation.pushHash(app.model.list.itemIdSerializer().toString(nextContent.id));
		this.switch(app, previous, next);
	}

	jump(app: App<P, A, Z, I>, item: string): void {
		const previous: number = app.current.get();
		const id: I = app.model.list.itemIdSerializer().parse(item);
		if (!id) {
			const error = new Error(`Invalid item id: ${item}`);
			app.model.settings.appendError(error);
			throw error;
		}
		const next: number = app.current.jump(id);
		app.navigation.pushHash(item);
		if (next >= 0) { this.switch(app, previous, next); }
	}

	firstJump(app: App<P, A, Z, I>, item: string) {
		const previous: number = app.current.get();
		const id: I = app.model.list.itemIdSerializer().parse(item);
		if (!id) {
			const error = new Error(`Invalid item id: ${item}`);
			app.model.settings.appendError(error);
			throw error;
		}
		const next: number = app.current.jump(id);
		app.navigation.replaceHash(item);
		if (next >= 0) { this.switch(app, previous, next); }
	}
	
	navigate(app: App<P, A, Z, I>, item: string) {
		const previous: number = app.current.get();
		const id: I = app.model.list.itemIdSerializer().parse(item);
		if (!id) {
			const error = new Error(`Invalid item id: ${item}`);
			app.model.settings.appendError(error);
			throw error;
		}
		const next: number = app.current.jump(id);
		app.navigation.updateHash(item);
		if (next >= 0) { this.switch(app, previous, next); }
	}

	switch(app: App<P, A, Z, I>, previous: number, next: number): Promise<void> {
		app.view.item.displayLoading("loading");
		app.view.list.displayCurrent(next);
		return Promise.all([
			app.content.get(next),
			app.content.get(previous),
			app.model.list.getItemIndexMap(),
		]).then(([nextContent, prevContent, indices]: [next: ContentItem<I>, previous: ContentItem<I>, indices: string[]]) => {
			const current: number = app.current.get();
			const prevTest: number = app.current.getAdjacent(-1);
			const nextTest: number = app.current.getAdjacent(1);
			const userData: Annotations = app.userdata.get(next);
			app.view.header.displayPrevious(prevTest!==current);
			app.view.header.displayNext(nextTest!==current);
			app.view.header.displayFlag(userData?.flagged);
			this._switch(app, previous, next, nextContent, prevContent);
			app.view.header.displayPosition(
				app.current.getPosition(),
				app.current.getVisible().length
			);
			app.model.state.attempt.updateState({ current: { id: indices[next] } });
			app.view.item.rebindKeyboard(app, nextContent.type);
			app.view.item.displayLoading("done");
			[1, -1, 2, -2]
				.map((offset: number) => app.current.getAdjacent(offset))
				.forEach((index: number) => app.content.get(index));
		}).catch((error: any) => {
			app.model.settings.appendError(error);
			app.view.item.displayLoading("error");
		});
	}

	protected abstract _switch(app: App<P, A, Z, I>, previous: number, next: number, nextContent: ContentItem<I>, previousContent: ContentItem<I>): void

	reload(app: App<P, A, Z, I>): Promise<void> {
		const current: number = app.current.get();
		return app.content.remove(current).then(() => this.switch(app, current, current));
	}
}

export class NullNavigationController<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> extends NavigationController<P, A, Z, I> {
	_switch(app: App<P, A, Z, I>, previous: number, next: number, nextContent: ContentItem<I>, previousContent: ContentItem<I>): void {
		throw new Error("Method not implemented.");
	}
}

export class PqNavigationController<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> extends NavigationController<P, A, Z, I> {
	protected _switch(app: PqApp<P, A, Z, I>, previous: number, next: number, nextContent: ContentItem<I>, previousContent: ContentItem<I>): void {
		const bindTarget: EventTarget = app.view.getBindTarget();
		const userData: Annotations = app.userdata.get(next);
		const sameContexts: ContentItem<I>["contexts"] = (previous !== 0 || next !== 0) ?
			app.model.item.getSameContexts(nextContent, previousContent) :
			[];
		const sameIds = sameContexts.map(c=>c.id);
		app.view.header.displayFinish(next==app.current.last());
		app.timer.switch(previous, next, {
			contexts_viewed: nextContent.contexts.reduce((o, c) => ({
				...o, [c.id]: !sameIds.includes(c.id)
			}), {})
		});
		app.view.header.displayItem(nextContent);
		app.view.item.displayItemContent(nextContent, bindTarget);
		app.view.item.displayItemSelectedAnswer(nextContent, bindTarget, userData?.answer);
		app.view.item.displayItemSameContext(nextContent, sameContexts);
		app.view.item.displayItemRendered(nextContent, app.getMathJax());
		app.view.item.displayItemAlerts(nextContent);
	}
}

export class FlashNavigationController<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> extends NavigationController<P, A, Z, I> {
	protected _switch(app: FlashApp<P, A, Z, I>, previous: number, next: number, nextContent: ContentItem<I>, previousContent: ContentItem<I>): void {
		const bindTarget: EventTarget = app.view.getBindTarget();
		const total: number = app.timer.total([next]);
		app.timer.switch(previous, next, { front: true });
		app.current.setAnswerVisible(false);
		app.view.header.displayCurrentTime(total);
		app.view.header.displayItem(nextContent);
		app.view.header.displayFlip(true);
		app.view.item.displayCardFront();
		app.view.item.displayItemContent(nextContent, bindTarget);
		app.view.item.displayItemRendered(nextContent, app.getMathJax());
		app.view.item.displayItemAlerts(nextContent);
	}
}
